import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { searchTutors } from '@/lib/tutor-api';
import { addToShortlist, removeFromShortlist, fetchMyParentProfile } from '@/lib/parent-api';
import { EDUCATOR_SUBJECTS, type EducatorSubject, type GradeLevel, type PublicTutorCard } from '@/lib/schemas/tutor-profile';
import { Button } from '@/components/ui/button';
import { Search, SlidersHorizontal, Sparkles, FilterX, X } from 'lucide-react';
import type { Filters } from './search';
import {
  PAGE_SIZE,
  SEARCH_DEBOUNCE_MS,
  TutorCardSkeleton,
  TutorCard,
  TutorSearchFilters,
} from './search';

export function SearchPage() {
  const { accessToken } = useAuth();
  
  // Set browser title on mount
  useEffect(() => {
    document.title = 'Find Tutors | CosmoBud';
  }, []);

  const [filters, setFilters] = useState<Filters>({
    subject: '',
    gradeLevel: '',
    name: '',
    language: '',
    gender: '',
    minHourlyInr: '',
    maxHourlyInr: '',
  });

  const [debouncedText, setDebouncedText] = useState({
    name: '',
    minHourlyInr: '',
    maxHourlyInr: '',
  });

  const [results, setResults] = useState<PublicTutorCard[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showBanner, setShowBanner] = useState(() => {
    try {
      return localStorage.getItem('cosmobud_hide_search_banner') !== 'true';
    } catch {
      return true;
    }
  });

  const handleDismissBanner = () => {
    setShowBanner(false);
    try {
      localStorage.setItem('cosmobud_hide_search_banner', 'true');
    } catch {
      // ignore
    }
  };

  // Load saved tutors from parent profile
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const profile = await fetchMyParentProfile(accessToken);
        if (!cancelled) {
          setSaved(new Set(profile.shortlistedTutorIds));
        }
      } catch {
        // Silently ignore errors loading saved tutors
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Debounce text/numerical inputs (name, min/max rates) to prevent spamming APIs
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText({
        name: filters.name,
        minHourlyInr: filters.minHourlyInr,
        maxHourlyInr: filters.maxHourlyInr,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [filters.name, filters.minHourlyInr, filters.maxHourlyInr]);

  const fetchPage = useCallback(
    async (
      subj: EducatorSubject | '',
      grade: GradeLevel | '',
      lang: string,
      gend: string,
      nameStr: string,
      minPrice: string,
      maxPrice: string,
      p: number,
      append: boolean,
    ) => {
      if (!accessToken) return;
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | number> = {
          ...(subj ? { subject: subj } : {}),
          ...(grade ? { gradeLevel: grade } : {}),
          ...(nameStr.trim() ? { name: nameStr.trim() } : {}),
          ...(lang ? { language: lang } : {}),
          ...(gend ? { gender: gend } : {}),
          page: p,
          pageSize: PAGE_SIZE,
        };

        const minVal = parseInt(minPrice.trim(), 10);
        if (!isNaN(minVal)) params.minHourlyInr = minVal;
        
        const maxVal = parseInt(maxPrice.trim(), 10);
        if (!isNaN(maxVal)) params.maxHourlyInr = maxVal;

        const data = await searchTutors(accessToken, params);
        setResults((prev) => (append ? [...prev, ...data.results] : data.results));
        setTotal(data.total);
        setPage(data.page);
      } catch (err: unknown) {
        const message = err instanceof ApiError ? err.message : String(err);
        console.error('[SearchPage] Tutor fetch error:', message, err);
        setError(err instanceof ApiError ? err.message : 'Failed to load tutors');
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  // Trigger search fetch whenever standard filters or debounced text/numerical inputs change
  useEffect(() => {
    void fetchPage(
      filters.subject,
      filters.gradeLevel,
      filters.language,
      filters.gender,
      debouncedText.name,
      debouncedText.minHourlyInr,
      debouncedText.maxHourlyInr,
      1,
      false,
    );
  }, [
    fetchPage,
    filters.subject,
    filters.gradeLevel,
    filters.language,
    filters.gender,
    debouncedText.name,
    debouncedText.minHourlyInr,
    debouncedText.maxHourlyInr,
  ]);

  const handleFilterChange = useCallback((key: keyof Filters, value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      subject: '',
      gradeLevel: '',
      name: '',
      language: '',
      gender: '',
      minHourlyInr: '',
      maxHourlyInr: '',
    });
  }, []);

  const handleLoadMore = () => {
    void fetchPage(
      filters.subject,
      filters.gradeLevel,
      filters.language,
      filters.gender,
      debouncedText.name,
      debouncedText.minHourlyInr,
      debouncedText.maxHourlyInr,
      page + 1,
      true,
    );
  };

  // Toggle saving to parent shortlist (Save/Unsave shortcut)
  const handleToggleSave = async (tutorId: string) => {
    if (!accessToken) return;
    const isSaved = saved.has(tutorId);
    try {
      if (isSaved) {
        await removeFromShortlist(accessToken, tutorId);
        setSaved((s) => {
          const next = new Set(s);
          next.delete(tutorId);
          return next;
        });
      } else {
        await addToShortlist(accessToken, tutorId);
        setSaved((s) => {
          const next = new Set(s);
          next.add(tutorId);
          return next;
        });
      }
    } catch (err) {
      console.error('[SearchPage] Shortlist toggle error:', err);
    }
  };

  const hasMore = results.length < total;
  const isAnyFilterApplied =
    !!(filters.subject ||
    filters.gradeLevel ||
    filters.name ||
    filters.language ||
    filters.gender ||
    filters.minHourlyInr ||
    filters.maxHourlyInr);

  return (
    <div className="w-full max-w-6xl mx-auto py-2 space-y-6 animate-fade-in">
      {/* 1. Space-themed Hero/Header Banner */}
      {showBanner && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-brand-700 to-accent-600 p-6 md:p-8 text-white shadow-lg">
          <div className="absolute top-0 right-0 -mt-6 -mr-6 h-36 w-36 rounded-full bg-white/10 blur-xl" />
          <div className="absolute bottom-0 left-1/3 -mb-10 h-44 w-44 rounded-full bg-accent-500/20 blur-2xl" />

          {/* Close button */}
          <button
            onClick={handleDismissBanner}
            className="absolute top-4 right-4 z-20 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors cursor-pointer"
            aria-label="Dismiss banner"
          >
            <X className="h-4.5 w-4.5" />
          </button>

          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold backdrop-blur-md">
              <Sparkles className="h-3.5 w-3.5 text-amber-300 animate-pulse" />
              <span>CosmoBud Verified Tutors</span>
            </div>
            <h1 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight">
              Find the Perfect Educator
            </h1>
            <p className="mt-2 text-sm md:text-base text-brand-100 font-medium leading-relaxed">
              Connect with certified, expert educators specializing in Mathematics, Science, AI & English. Handpicked to help your child excel and build confidence.
            </p>
          </div>
        </div>
      )}

      {/* 2. Search Box and Quick Subject Pills */}
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 transition-all p-1.5 gap-2">
          <div className="flex items-center flex-1 px-3 gap-2">
            <Search className="h-5 w-5 text-slate-400 dark:text-slate-505 shrink-0" />
            <input
              type="text"
              placeholder="Search by tutor name..."
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              className="w-full bg-transparent border-0 outline-none text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-505 text-sm py-1.5 focus:outline-none focus:ring-0"
            />
          </div>
          
          <Button
            variant="outline"
            size="sm"
            className="md:hidden flex items-center gap-1.5 border-slate-200 dark:border-slate-800 h-9"
            onClick={() => setShowMobileFilters(true)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span>Filters</span>
          </Button>
        </div>

        {/* Quick Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          <span className="text-xs font-semibold text-slate-450 dark:text-slate-505 uppercase tracking-wider shrink-0 mr-1 select-none">
            Popular:
          </span>
          <button
            onClick={() => handleFilterChange('subject', '')}
            className={`rounded-full px-3.5 py-1 text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
              filters.subject === ''
                ? 'bg-brand-600 border-brand-600 text-white dark:bg-brand-500 dark:border-brand-500'
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-305 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            All Subjects
          </button>
          {EDUCATOR_SUBJECTS.map((subj) => (
            <button
              key={subj}
              onClick={() => handleFilterChange('subject', subj)}
              className={`rounded-full px-3.5 py-1 text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                filters.subject === subj
                  ? 'bg-brand-600 border-brand-600 text-white dark:bg-brand-500 dark:border-brand-500'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-305 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
              }`}
            >
              {subj}
            </button>
          ))}
        </div>
      </div>

      {/* 3. Core Content Layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
        {/* Desktop Sidebar Filters */}
        <aside className="hidden md:block md:col-span-4 lg:col-span-3">
          <TutorSearchFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            isAnyFilterApplied={isAnyFilterApplied}
            isMobile={false}
          />
        </aside>

        {/* Main Tutors Listings Panel */}
        <section className="col-span-1 md:col-span-8 lg:col-span-9 space-y-4">
          {error && (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50/50 p-4 text-sm text-red-705 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-450 flex gap-2.5 items-start">
              <span className="font-bold shrink-0">⚠️ Error:</span>
              <span>{error}</span>
            </div>
          )}

          {/* Results Metadata */}
          {!loading && !error && (
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400 dark:text-slate-500">
                {total === 0 ? (
                  'No educators found'
                ) : (
                  <>
                    Showing <span className="font-semibold text-slate-500 dark:text-slate-400">{results.length}</span> of{' '}
                    <span className="font-semibold text-slate-500 dark:text-slate-400">{total}</span> verified educator{total === 1 ? '' : 's'}
                  </>
                )}
              </div>
              {isAnyFilterApplied && (
                <button
                  onClick={handleClearFilters}
                  className="md:hidden text-xs text-brand-600 dark:text-brand-400 font-semibold flex items-center gap-1 cursor-pointer"
                >
                  Reset filters
                </button>
              )}
            </div>
          )}

          {/* Listings Container */}
          <ul className="space-y-4">
            {results.map((tutor) => (
              <li key={tutor.id}>
                <TutorCard
                  tutor={tutor}
                  isSaved={saved.has(tutor.id)}
                  onToggleSave={handleToggleSave}
                />
              </li>
            ))}
          </ul>

          {/* Skeleton placeholders loading state */}
          {loading && (
            <div className="space-y-4">
              <TutorCardSkeleton />
              <TutorCardSkeleton />
              <TutorCardSkeleton />
            </div>
          )}

          {/* Empty search results state */}
          {!loading && results.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900/30 p-12 text-center shadow-sm space-y-4">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-105 dark:bg-slate-800 text-slate-400">
                <FilterX className="h-6 w-6" />
              </div>
              <div className="space-y-2">
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">No Educators Found</h3>
                <p className="text-sm text-slate-505 dark:text-slate-400 max-w-sm mx-auto">
                  We couldn't find any educators matching your exact filters. Try broadening your criteria or search by different parameters.
                </p>
              </div>
              {isAnyFilterApplied && (
                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                  Clear all filters
                </Button>
              )}
            </div>
          )}

          {/* Load More Button */}
          {!loading && hasMore && (
            <div className="pt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                className="w-full sm:w-auto px-6 h-10 font-bold border-slate-200 dark:border-slate-800"
              >
                Load more educators
              </Button>
            </div>
          )}
        </section>
      </div>

      {/* 4. Mobile Filter Overlay Drawer */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm md:hidden animate-in fade-in duration-200">
          <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-xl animate-in zoom-in duration-200 max-h-[85vh] flex flex-col">
            <button
              onClick={() => setShowMobileFilters(false)}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-slate-105 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-505 cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
            
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2">
              <SlidersHorizontal className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              <span>Filters</span>
            </h3>

            <div className="flex-1 overflow-y-auto pr-1">
              <TutorSearchFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                isAnyFilterApplied={isAnyFilterApplied}
                isMobile={true}
              />
            </div>

            <div className="mt-5 border-t border-slate-100 dark:border-slate-800 pt-4 flex gap-2">
              {isAnyFilterApplied && (
                <Button
                  variant="outline"
                  onClick={() => {
                    handleClearFilters();
                    setShowMobileFilters(false);
                  }}
                  className="flex-1 font-bold h-10"
                >
                  Clear All
                </Button>
              )}
              <Button
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 font-bold h-10"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
